import React, { useRef, useMemo, useEffect } from 'react'
import { usePopper } from 'react-popper'

import { useConstCallback, useOnClickOutside, useMinZIndex } from '~hooks'
import { useBreakpointEnding, Breakpoint, ZLayers } from '~theme'
import { Portal } from '~components/Portal'

import { DropdownProps } from './Dropdown.types'
import * as Styled from './Dropdown.styles'
import dropdownPopperModifiers from './Dropdown.modifiers'

const DROPDOWN_PORTAL_ID = 'dropdown-root'

const Dropdown: React.FC<DropdownProps> = ({
  children,
  isOpen,
  onOpenChange,
  contentRootRef,
  shouldDismissOnClick = true,
  strategy = 'fixed',
  placement = 'bottom-start',
  on = 'click',
  isContentSameWidthAsTrigger = false,
  shouldHideOnInvisibleTrigger = true,
  zIndex: requestedZIndex = ZLayers.uiElement,
}) => {
  const [triggerContent, dropdownContent] = children
  // We force the event to be click on mobile devices
  const isMobile = useBreakpointEnding(Breakpoint.md)

  const { zIndex, ZIndexContextProvider } = useMinZIndex(requestedZIndex)

  const triggerEvent = useMemo(() => (isMobile ? 'click' : on), [isMobile, on])

  const triggerRef = useRef<HTMLDivElement>(null)
  const popperRef = useRef<HTMLDivElement>(null)

  const popperModifiers = useMemo(() => dropdownPopperModifiers({ sameWidth: isContentSameWidthAsTrigger }), [isContentSameWidthAsTrigger])

  const { styles, attributes, update } = usePopper(triggerRef.current, popperRef.current, {
    placement,
    strategy,
    modifiers: popperModifiers,
  })

  useEffect(() => {
    update?.()
  }, [isOpen])

  const onTriggerClickHandler = useConstCallback(
    (event: React.MouseEvent) => {
      if (triggerEvent === 'click') {
        onOpenChange(!isOpen)
        event.stopPropagation()
        event.preventDefault()
      }
    },
    [triggerEvent, isOpen],
  )

  const onPopoverClick = useConstCallback(
    (event: React.MouseEvent) => {
      if (shouldDismissOnClick) {
        onOpenChange(false)
        event.stopPropagation()
        event.preventDefault()
      }
    },
    [shouldDismissOnClick],
  )

  const onTriggerHoverEnterHandler = useConstCallback(() => triggerEvent === 'hover' && onOpenChange(true), [triggerEvent])
  const onTriggerHoverLeaveHandler = useConstCallback(() => triggerEvent === 'hover' && onOpenChange(false), [triggerEvent])

  useOnClickOutside([triggerRef, popperRef, DROPDOWN_PORTAL_ID], () => onOpenChange(false), isOpen)

  return (
    <Styled.Container onMouseEnter={onTriggerHoverEnterHandler} onMouseLeave={onTriggerHoverLeaveHandler}>
      <Styled.TriggerContainer ref={triggerRef} onClick={onTriggerClickHandler} role="presentation">
        {triggerContent}
      </Styled.TriggerContainer>
      <Portal rootId={DROPDOWN_PORTAL_ID} rootRef={contentRootRef}>
        <Styled.PopoverContainer
          hideOnHiddenReference={shouldHideOnInvisibleTrigger}
          zIndex={zIndex}
          ref={popperRef}
          onClick={onPopoverClick}
          style={isOpen ? styles.popper : {}}
          {...(isOpen ? attributes.popper : {})}>
          {isOpen && (
            <Styled.PopoverContentContainer>
              <ZIndexContextProvider>{dropdownContent}</ZIndexContextProvider>
            </Styled.PopoverContentContainer>
          )}
        </Styled.PopoverContainer>
      </Portal>
    </Styled.Container>
  )
}

Dropdown.displayName = 'Dropdown'
export default Dropdown
